import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { delay } from '@sequencehq/utils'

import {
  APPROVAL_OUTCOME_MAX_WAIT_MS,
  APPROVAL_OUTCOME_POLL_INTERVAL_MS
} from './approvalOutcome.constants'
import {
  readApprovalOutcome,
  type ApprovalOutcome
} from './readApprovalOutcome'

/**
 * Watches a quote approval run until it has done something worth telling
 * somebody about, and reports what that was.
 *
 * Answers `PENDING` if the run hasn't decided by the deadline - the publish
 * request itself succeeded either way, so the caller falls back to saying the
 * quote went for approval rather than claiming something went wrong.
 *
 * The client is called directly rather than through `useQuery`, because this
 * runs inside a submit handler and nothing renders off it.
 */
export const waitForApprovalOutcome = async ({
  executionId,
  deadline = Date.now() + APPROVAL_OUTCOME_MAX_WAIT_MS
}: {
  executionId: string
  /** When to stop asking. Carried through the recursion, not for callers. */
  deadline?: number
}): Promise<ApprovalOutcome> => {
  const outcome = readApprovalOutcome(await readExecution(executionId))

  if (outcome !== 'PENDING') return outcome
  if (Date.now() >= deadline) return 'PENDING'

  await delay(APPROVAL_OUTCOME_POLL_INTERVAL_MS)

  return waitForApprovalOutcome({ executionId, deadline })
}

/**
 * The run may not be readable the instant publish returns, and a blip part way
 * through shouldn't end the wait - either way we've simply not been told
 * anything yet, so we ask again.
 */
const readExecution = async (id: string) => {
  try {
    const { data } = await dashboardv99990101Client.getAutomationExecution({
      id
    })

    return data ?? undefined
  } catch {
    return undefined
  }
}
